import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Spin } from '@douyinfe/semi-ui';
import QRCode from 'qrcode';
import styles from './index.module.scss';

/**
 * 基础二维码组件
 * 使用现代qrcode库生成二维码
 */
const QRCodeGenerator = React.forwardRef(({
    text = '',
    size = 256,
    colorDark = '#000000',
    colorLight = '#ffffff',
    correctLevel = 'M', // L, M, Q, H
    className = '',
    style = {},
    onGenerated = null,
    onError = null
}, ref) => {
    const canvasRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 错误级别映射
    const getCorrectLevel = (level) => {
        const levels = {
            'L': 'low',
            'M': 'medium',
            'Q': 'quartile',
            'H': 'high'
        };
        return levels[level] || 'medium';
    };

    const generateQRCode = useCallback(async () => {
        if (!text || !canvasRef.current) return;

        setLoading(true);
        setError(null);

        try {
            // 使用现代qrcode库生成二维码到canvas
            await QRCode.toCanvas(canvasRef.current, text, {
                width: size,
                color: {
                    dark: colorDark,
                    light: colorLight,
                },
                errorCorrectionLevel: getCorrectLevel(correctLevel),
                margin: 1
            });

            setLoading(false);

            if (onGenerated) {
                onGenerated(canvasRef.current);
            }

        } catch (err) {
            console.error('QR Code generation failed:', err);
            setError(err.message);
            setLoading(false);

            if (onError) {
                onError(err);
            }
        }
    }, [text, size, colorDark, colorLight, correctLevel, onGenerated, onError]);

    // 当参数改变时重新生成二维码
    useEffect(() => {
        generateQRCode();
    }, [generateQRCode]);

    // 获取二维码数据URL
    const toDataURL = useCallback(() => {
        if (!canvasRef.current) return null;
        return canvasRef.current.toDataURL('image/png');
    }, []);

    // 暴露方法给父组件（通过ref）
    React.useImperativeHandle(ref, () => ({
        toDataURL,
        regenerate: generateQRCode,
        getCanvas: () => canvasRef.current
    }), [toDataURL, generateQRCode]);

    if (error) {
        return (
            <div className={`${styles.qrError} ${className}`} style={style}>
                <div className={styles.errorMessage}>
                    二维码生成失败: {error}
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.qrContainer} ${className}`} style={style}>
            {loading && (
                <div className={styles.loading}>
                    <Spin size="large" />
                </div>
            )}
            <canvas
                ref={canvasRef}
                className={styles.qrCode}
                style={{
                    width: size,
                    height: size,
                    opacity: loading ? 0.5 : 1,
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
            />
        </div>
    );
});

// 导出组件和带Logo版本
export default QRCodeGenerator;
export { default as QRCodeWithLogo } from './QRCodeWithLogo';
